import { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectors, lib } from '@gocanvas/client'
const { conditionsSelector } = selectors
const { isEligibleKeyField } = lib
import { FieldIcon } from 'apps/builder/components/workspace/fields/field-icons'
import {
  FancyCheckbox,
  InlineTooltip,
} from 'apps/builder/components/settings/modal'
import {
  faEllipsisH,
  faCog,
  faPlus,
  faCodeBranch,
  faRectangleLandscape,
  faTable,
  faBolt,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faPageBreak,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons'
import Select, { components } from 'react-select'
import {
  deleteSheet,
  deleteChildSheet,
  deleteLoopSheet,
  convertListToScreen,
  convertScreenToList,
} from 'apps/builder/store/actions/sheets'
import {
  createNewEntry,
  insertNewScreen,
  copySheet,
  splitSheet,
  addTable,
  copyTableOrSimpleLoop,
} from 'apps/builder/store/actions/workspace'
import {
  classifyFieldByLabel,
  getFieldBubble,
  isTrueStringMatch,
  createGuid,
  sheetType,
  guidNumber,
  TABLE_SHEET,
  ADVANCED_LOOP_SHEET,
  LOOP_SHEET,
  NORMAL_SHEET,
  LIST_SHEET,
} from 'apps/builder/lib/utils'
import { onClickOutside } from 'lib/customHooks'
import {
  openScreenSettings,
  toggleModal,
} from 'apps/builder/store/actions/settings'
import {
  entryTypesSelector,
  sectionsSelector,
  sheetsSelector,
  keyFieldTypesSelector,
  referenceDataEntries,
  builderTypeSelector,
  whiteLabelSelector,
  subSheetsSelector,
  entriesSelector,
  newWorkspaceFeatureFlagSelector,
  liveDataEntries,
} from 'apps/builder/store/selectors'
import { useHotkeys } from 'react-hotkeys-hook'
import Flatpickr from 'react-flatpickr'
import { findChildSheets } from 'apps/builder/components/workspace/sheets/helpers'
import { useIsLoopable } from 'apps/builder/lib/hooks/useIsLoopable'
import ConditionIcon from 'apps/builder/assets/icons/conditions.svg'
import ReferenceDataIcon from 'apps/builder/assets/icons/reference-data.svg'
import LiveDataIcon from 'apps/builder/assets/icons/live-data.svg'
import { leftPanelTabs } from 'apps/builder/store/reducers/settings/appSettings'
import { getEntryLabelLimit } from 'apps/builder/components/workspace/fields/helpers'

export const Editable = ({
  update = () => {},
  placeholder,
  autoFocus,
  value = '',
  className,
  limit,
  onBlur = () => {},
}) => {
  const [inputValue, setInputValue] = useState(value)
  const inputRef = useRef(null)
  const isNewValue = value !== inputValue

  useEffect(() => {
    if (inputRef && autoFocus && isNewValue) {
      inputRef.current.focus()
    }

    setInputValue(value)
  }, [inputRef, value])

  const updateValue = () => {
    if (isNewValue) {
      update(inputValue)

      // This covers an edge case where the inputValue is changed, but code
      // elsewhere results in the updated value being the same as the original.
      // I don't believe this will ever be a different value than the inputValue
      // but if we ever run into issues down the line, this would be the place to look.
      setInputValue(value)
    }
  }

  const handleBlur = () => {
    updateValue()
    onBlur(inputValue)
  }

  const updateInputValue = ({ target }) => {
    setInputValue(target.value)
  }

  const onKeyUp = ({ key }) => {
    if (key === 'Enter') {
      inputRef.current.blur()
    }
  }

  return (
    <input
      type="text"
      maxLength={limit}
      ref={inputRef}
      onMouseLeave={() => {
        inputRef.current.blur()
      }}
      onKeyUp={onKeyUp}
      onBlur={handleBlur}
      onWheel={e => e.target.blur()}
      placeholder={placeholder}
      value={inputValue}
      onChange={updateInputValue}
      className={className}
    />
  )
}

// TODO: This can probably be combined with the Editable component
export const EditableLabel = ({
  update = () => {},
  autoFocus = false,
  label,
  placeholder,
  onBlur = update,
  className = '',
  type,
  limit,
  screenType = '',
}) => {
  const defaultLabel =
    placeholder || (!type && `Type ${screenType} name`) || `New ${type}`
  return (
    <Editable
      placeholder={defaultLabel}
      autoFocus={autoFocus}
      limit={limit}
      value={label}
      onBlur={onBlur}
      update={update}
      className={`editable--label editable--label--inputs ${className}`}
    />
  )
}

const FieldOption = props => {
  const { label } = props.data
  const type = props.data.type ? props.data.type : classifyFieldByLabel(label)
  return (
    <div className="field--option" data-testid={`match-${label}`}>
      <components.Option {...props}>
        <FieldIcon type={type} />
        <div className="field--content">{props.children}</div>
      </components.Option>
    </div>
  )
}

export const BasicTooltip = ({ label, description = null, className }) => {
  return (
    <div className={className}>
      {label ? <h3>{label}</h3> : ''}
      {description ? <p>{description}</p> : ''}
    </div>
  )
}

export const FoundryTooltip = ({
  label,
  direction = 'bottom',
  size = 'small',
}) => {
  return (
    <div className={`foundry--tooltip tooltip--${direction} tooltip--${size}`}>
      {label}
    </div>
  )
}

export const PillButton = ({
  icon = null,
  iconPosition = 'left',
  label = '',
  onClick = () => {},
  className = null,
}) => {
  const icons = {
    screen: faRectangleLandscape,
    table: faTable,
    field: faPlus,
    'page-break': faPageBreak,
  }
  return (
    <button
      className={`foundry--pill-button ${
        icon && `pill-button--icon-${iconPosition}`
      } ${className}`}
      type="button"
      onClick={onClick}
    >
      {icon && (
        <FontAwesomeIcon
          className="foundry-icon pill-button--icon"
          icon={icons[icon]}
        />
      )}
      <span className="pill-button--label">{label}</span>
    </button>
  )
}

export const FieldBubble = ({ className, onClick, ...entry }) => {
  const { fieldBubbleText, completedField } = getFieldBubble(entry)
  return (
    !completedField && (
      <>
        <div className="bubble--border"></div>
        <div className={className}>
          <button className={`foundry--button button--text`} onClick={onClick}>
            <span className="button--label">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <span className="button--label">{fieldBubbleText}</span>
          </button>
        </div>
      </>
    )
  )
}

export const FieldContextToolTip = ({ className, onClick, ...entry }) => {
  const { fieldBubbleText, completedField } = getFieldBubble(entry)
  return (
    !completedField && (
      <div className="field-context--alert">
        <InlineTooltip
          icon={faExclamationTriangle}
          direction="top"
          tooltip={fieldBubbleText}
        />
      </div>
    )
  )
}

export const FieldBubbleErrorState = ({ className, type, ...entry }) => {
  const validationErrorMessageByType = {
    Sum: (
      <>
        To use a Sum field you must first add a Number or Calculation to a
        previous Screen.{' '}
        <WhiteLabelCheck>
          <a href="https://help.gocanvas.com/hc/en-us/articles/25370428093719-Summarizing-Values-from-a-Repeating-Screen-Type">
            Learn More
          </a>
        </WhiteLabelCheck>
      </>
    ),
  }

  const errorMessage = validationErrorMessageByType[type]

  return (
    <>
      <div className="bubble--border--error"></div>
      <div
        className={`error--tooltip error--tooltip--content tooltip--type-${className}`}
      >
        {errorMessage}
      </div>
    </>
  )
}

export const WhiteLabelCheck = ({ children }) => {
  const isWhiteLabeled = useSelector(whiteLabelSelector)

  return !isWhiteLabeled && children
}

//TODO: convert these colors to foundry vars
export const selectStyles = {
  container: (provided, state) => ({
    ...provided,
    maxWidth: '20em',
    '+ label': {
      color: state.isDisabled
        ? '#92999D'
        : state.isFocused
        ? '#039de7'
        : '#414042',
      height: '1em',
      left: '1em',
      letterSpacing: '0.053em',
      background: '#ffffff',

      fontWeight: '600',
      fontSize: '0.8125em',
    },
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    paddingLeft: '1em',
  }),
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    borderColor: state.isFocused ? '#039de7' : '#92999d',
    borderRadius: '0.1875em',
    cursor: 'pointer',
    '&:hover': {
      borderColor: state.isFocused ? '#039de7' : '#92999d',
    },
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    transition: 'color 150ms, transform 0.2s ease-in-out',
    padding: '0.75em',
    color: state.isFocused ? '#039de7' : '#92999d',
    '&:hover': { color: state.isFocused ? '#039de7' : '#92999d' },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  input: provided => ({
    ...provided,
    padding: '0.5em 1em',
    paddingLeft: '0',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    left: '1em',
  }),
  menu: (provided, state) => ({
    ...provided,
    'z-index': 10,
    margin: '0',
    padding: '0',
  }),
  noOptionsMessage: (provided, state) => ({
    color: '#92999D',
    padding: '0.75em 1.25em',
  }),
  menuList: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
  }),
  option: (provided, state) => ({
    ...provided,
    display: 'flex',
    width: '100%',
    padding: '0.25em 1em',
    color: '#414042',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    fontSize: '1em',
    lineHeight: '2em',
    '.field--content': {
      fontWeight: state.isSelected ? 'bold' : 'normal',
    },
    '.field--icon': {
      color: '#414042',
      width: '1.5em',
      paddingTop: '0.25em',
      marginRight: '0.5em',
    },
    ':hover': {
      backgroundColor: '#f0f0f0',
      color: '#414042',
      '.field--icon': {
        color: '#414042',
      },
    },
    ':active': {
      '.field--content': {
        fontWeight: 'bold',
      },
    },
  }),
}

export const altSelectStyles = {
  container: (provided, state) => ({
    ...provided,
    fontWeight: 400,
    marginRight: '1em',
    marginBottom: '0.5em',
    '&:firstChild': {
      marginLeft: '0',
    },
  }),
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    borderWidth: '0 0 0.125em 0',
    borderStyle: 'solid',
    borderRadius: '0',
    borderColor: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
    backgroundColor: 'transparent',
    cursor: state.selectProps.isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': {
      borderColor: state.selectProps.menuIsOpen
        ? '#039DE7'
        : state.hasValue
        ? '#1682A2'
        : state.isFocused
        ? '#414042'
        : '#92999d',
    },
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '0',
    flexWrap: 'nowrap',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    position: 'relative',
    transform: 'none',
    fontSize: '1.125em',
    lineHeight: '1.3125em',
    fontWeight: '400',
    minWidth: '100%',
    paddingRight: '0.25em',
    color: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    position: 'relative',
    transform: 'none',
    fontSize: '1.125em',
    lineHeight: '1.3125em',
    fontWeight: '400',
    color: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    minWidth: '100%',
    whiteSpace: 'nowrap',
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    padding: '0',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: '0',
    color: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
    transform: state.selectProps.menuIsOpen ? 'scaleY(-1)' : 'scaleY(1)',
    transition: 'transform 0.15s linear',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided, state) => ({
    ...provided,
    marginTop: '0',
    minWidth: 'fit-content',
    whiteSpace: 'nowrap',
  }),
  menuList: (provided, state) => ({
    ...provided,
    paddingTop: '0',
    cursor: 'pointer',
  }),
  option: (provided, state) => ({
    ...provided,
    color: '#414042',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '1em',
    lineHeight: '2em',
    fontWeight: state.isSelected ? 'bold' : 'normal',
    '&:hover': {
      fontWeight: 'bold',
    },
    '&:active': {
      fontWeight: 'bold',
      backgroundColor: 'transparent',
    },
  }),
  groupHeading: (provided, state) => ({
    ...provided,
    color: '#92999D',
    fontSize: '1em',
    lineHeight: '2em',
  }),
}

export const andOrSelectStyle = {
  ...altSelectStyles,
  container: (provided, state) => ({
    ...provided,
    marginRight: '1em',
    marginBottom: '0.5em',
  }),
  control: (provided, state) => {
    const values = state.getValue()
    const selected = values[0] || { label: 'and' }
    const { label } = selected
    const borderRadius = label === 'and' ? '5%' : '50%'
    return {
      ...provided,
      boxShadow: 'none',
      borderWidth: '0.0625em',
      borderStyle: 'solid',
      borderRadius,
      borderColor: '#1257cd',
      backgroundColor: 'transparent',
      padding: '0.25em',
      cursor: 'pointer',
      width: '3em',
      height: '3em',
      '&:hover': {
        borderColor: '#1257cd',
        backgroundColor: '#f1fbff',
      },
    }
  },
  placeholder: (provided, state) => ({
    ...provided,
    padding: '0',
    marginLeft: '0',
    marginRight: '0',
    position: 'relative',
    transform: 'none',
    fontSize: '1.125em',
    lineHeight: '1.3125em',
    fontWeight: '400',
    width: '100%',
    maxWidth: '100%',
    color: '#1257cd',
    textAlign: 'center',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    padding: '0',
    marginLeft: '0',
    marginRight: '0',
    position: 'relative',
    transform: 'none',
    fontSize: '1.125em',
    lineHeight: '1.3125em',
    fontWeight: '400',
    width: '100%',
    maxWidth: '100%',
    color: '#1257cd',
    textAlign: 'center',
  }),
  dropdownIndicator: (provided, state) => ({
    display: 'none',
  }),
}

export const operatorSelectStyles = {
  ...altSelectStyles,
  container: (provided, state) => ({
    ...provided,
    marginRight: '1em',
  }),
  control: (provided, state) => {
    return {
      ...provided,
      boxShadow: 'none',
      borderWidth: '0.0625em',
      borderStyle: 'solid',
      borderRadius: '0.1875em',
      borderColor: '#1257cd',
      backgroundColor: 'transparent',
      padding: '0.25em',
      cursor: 'pointer',
      width: '1.875em',
      height: '1.875em',
      minHeight: '100%',
      '&:hover': {
        borderColor: '#1257cd',
        backgroundColor: '#f1fbff',
      },
    }
  },
  menu: (provided, state) => ({
    ...provided,
    left: '-0.2em',
    width: '125%',
  }),
  menuList: (provided, state) => ({
    ...provided,
    paddingBottom: 0,
    paddingTop: 0,
  }),
  option: (provided, state) => {
    return {
      ...provided,
      padding: '0.75em',
      lineHeight: '0.75em',
      cursor: 'pointer',
      backgroundColor: state.isSelected
        ? '#1257cd'
        : state.isFocused
        ? '#f0f0f0'
        : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#414042',
      ':hover': {
        backgroundColor: '#f0f0f0',
        color: '#414042',
      },
      ':active': {
        backgroundColor: '#1257cd',
        color: '#ffffff',
      },
    }
  },
  placeholder: (provided, state) => ({
    ...provided,
    padding: '0',
    marginLeft: '0',
    marginRight: '0',
    position: 'relative',
    transform: 'none',
    fontSize: '1em',
    lineHeight: '0.8em',
    fontWeight: '400',
    width: '100%',
    maxWidth: '100%',
    color: '#1257cd',
    textAlign: 'center',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    padding: '0',
    marginLeft: '0',
    marginRight: '0',
    position: 'relative',
    transform: 'none',
    fontSize: '1em',
    lineHeight: '0.8em',
    fontWeight: '400',
    width: '100%',
    maxWidth: '100%',
    color: '#1257cd',
    textAlign: 'center',
    marginBottom: '0.3em',
  }),
  dropdownIndicator: (provided, state) => ({
    display: 'none',
  }),
}

export const calculationSelectStyles = {
  container: (provided, state) => ({
    ...provided,
    marginRight: '1em',
    marginBottom: '0.5em',
    '&:firstChild': {
      marginLeft: '0',
    },
  }),
  control: (provided, state) => ({
    ...provided,
    boxShadow: 'none',
    borderWidth: '0 0 0.125em 0',
    borderStyle: 'solid',
    borderRadius: '0',
    borderColor: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
    backgroundColor: 'transparent',
    cursor: state.selectProps.isDisabled ? 'not-allowed' : 'pointer',
    '&:hover': {
      borderColor: state.selectProps.menuIsOpen
        ? '#039DE7'
        : state.hasValue
        ? '#1682A2'
        : state.isFocused
        ? '#414042'
        : '#92999d',
    },
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '0',
    flexWrap: 'nowrap',
  }),
  singleValue: (provided, state) => ({
    ...provided,
    position: 'relative',
    transform: 'none',
    fontSize: '1.125em',
    lineHeight: '1.3125em',
    fontWeight: '400',
    minWidth: '100%',
    paddingRight: '0.25em',
    color: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
  }),
  placeholder: (provided, state) => ({
    ...provided,
    position: 'relative',
    transform: 'none',
    fontSize: '1.125em',
    lineHeight: '1.3125em',
    fontWeight: '400',
    color: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    minWidth: '100%',
    whiteSpace: 'nowrap',
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    padding: '0',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: '0',
    color: state.selectProps.menuIsOpen
      ? '#039DE7'
      : state.hasValue
      ? '#1682A2'
      : state.isFocused
      ? '#414042'
      : '#92999d',
    transform: state.selectProps.menuIsOpen ? 'scaleY(-1)' : 'scaleY(1)',
    transition: 'transform 0.15s linear',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided, state) => ({
    ...provided,
    marginTop: '0',
  }),
  menuList: (provided, state) => ({
    ...provided,
    paddingTop: '0',
    cursor: 'pointer',
  }),
  menu: (provided, state) => ({
    ...provided,
    minWidth: 'fit-content',
    whiteSpace: 'nowrap',
  }),
  option: (provided, state) => {
    return {
      ...provided,
      padding: '0.75em',
      lineHeight: '0.75em',
      cursor: 'pointer',
      backgroundColor: state.isSelected
        ? '#039de7'
        : state.isFocused
        ? '#f0f0f0'
        : '#ffffff',
      color: state.isSelected ? '#ffffff' : '#414042',
      ':hover': {
        backgroundColor: '#f0f0f0',
        color: '#414042',
      },
      ':active': {
        backgroundColor: '#039de7',
        color: '#ffffff',
      },
    }
  },
  groupHeading: (provided, state) => ({
    ...provided,
    textTransform: 'capitalize',
    color: '#92999D',
    fontSize: '1em',
    lineHeight: '2em',
    borderBottom: '1px solid #9299D',
  }),
}

export const editableSelectStyle = {
  ...selectStyles,
  container: (provided, state) => ({
    ...provided,
    marginBottom: '0.25em',
  }),
  menu: (provided, state) => ({
    ...provided,
    zIndex: 3,
  }),
  groupHeading: (provided, state) => ({
    ...provided,
    color: '#92999D',
    fontSize: '1em',
    lineHeight: '1.25em',
    textTransform: 'initial',
  }),
}

export const FieldSelector = ({
  onChange,
  value,
  defaultValue,
  className = 'type--select',
  keyField,
  open = true,
  disabled,
}) => {
  const selector = keyField ? keyFieldTypesSelector : entryTypesSelector
  const entryTypes = useSelector(selector) || []
  const options = entryTypes.map(({ type, ...rest }) => ({
    value: type,
    label: type,
    type,
    placeholder: `New ${type}`,
    ...rest,
  }))

  const getFieldName = ({ value }) => {
    const customFieldNames = {
      Sum: 'Calculation',
    }
    const fieldName = customFieldNames[value] || value

    return options.find(({ value }) => value === fieldName)
  }

  const fieldName = getFieldName(value)

  return (
    <Select
      styles={selectStyles}
      components={{ Option: FieldOption }}
      value={fieldName}
      isDisabled={disabled}
      defaultValue={defaultValue || options[0]}
      defaultMenuIsOpen={open}
      className={className}
      classNamePrefix="react-select"
      onChange={({ value }) => {
        const { type, ...rest } = entryTypes.find(({ type }) => type === value)
        onChange({
          type,
          label: `New ${type}`,
          placeholder: `New ${type}`,
          ...rest,
        })
      }}
      options={options}
    />
  )
}

export const QuickField = ({
  position,
  sheetId,
  keyField,
  table,
  additionalAction = () => {},
}) => {
  const entryTypes = useSelector(entryTypesSelector)
  const allSheets = useSelector(sheetsSelector)
  const thisSheet = allSheets.find(({ id }) => isTrueStringMatch(id, sheetId))
  const dispatch = useDispatch()
  const inputRef = useRef(null)

  const findByLabel = (label = '') => {
    const match = entryTypes.find(
      ({ type }) => String(label).toLowerCase() == String(type).toLowerCase()
    )
    if (match) {
      return match.type
    }
  }

  const onSelect = label => {
    const type = findByLabel(label) || classifyFieldByLabel(label)
    const textField = entryTypes.find(({ type }) => type === 'Text')
    const entryType = entryTypes.find(entry => entry.type === type) || textField

    additionalAction()
    dispatch(
      createNewEntry({
        ...entryType,
        entry_type_id: entryType.id,
        sheet_id: sheetId,
        position,
        keyField,
        label,
        source: 'quick-field',
      })
    )
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef])

  const onKeyUp = ({ key }) => {
    if (key === 'Enter') {
      onSelect(inputRef.current.value)
      inputRef.current.value = ''
    }
  }
  return (
    <div className="select--wrapper" onClick={e => e.stopPropagation()}>
      <div className="field--wrapper quick-field">
        <input
          type="text"
          ref={inputRef}
          placeholder={`Type ${table ? 'column' : 'field'} name`}
          onKeyUp={onKeyUp}
          className="quick-field--input"
          maxLength={getEntryLabelLimit(thisSheet)}
        />
        <div className="quick-field--label">Press Enter to add</div>
      </div>
    </div>
  )
}

export const SheetActions = ({
  id,
  sectionId,
  multiSectionId,
  style = 0,
  sheetTypeId,
  depth = 0,
  multi_section,
  sheetControlRef,
}) => {
  const [open, setState] = useState(false)
  const dispatch = useDispatch()
  const entries = useSelector(entriesSelector)
  const sheets = useSelector(sheetsSelector)
  const isNewWorkspace = useSelector(newWorkspaceFeatureFlagSelector)
  const typeOfSheet = sheetType({
    style,
    multi_section,
    sheet_type_id: sheetTypeId,
  })
  const parentSheet = sheets.find(sheet =>
    isTrueStringMatch(sheet.multi_section, sectionId)
  )
  const sectionSheets = sheets.filter(({ section_id }) =>
    isTrueStringMatch(section_id, sectionId)
  )
  const subSheets = useSelector(subSheetsSelector)
  const childSheets = subSheets.filter(({ section_id }) =>
    isTrueStringMatch(section_id, multi_section)
  )
  const isLoopable = useIsLoopable(childSheets, depth)

  if (multiSectionId) {
    // Traverse to find all nested sheets
    sectionSheets.push(...findChildSheets(sheets, multiSectionId))
  }

  const sheetEntries = entries.filter(entry =>
    isTrueStringMatch(entry.sheet_id, id)
  )

  const toggleControls = () => setState(!open)

  const convertToLoopModal = e => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(
      toggleModal({
        activeModal: 'convertToLoop',
        activeEntry: '',
        activeSheet: id,
        activeLeftPanelTab: leftPanelTabs.SCREEN,
      })
    )
  }

  const convertToTableModal = e => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(
      toggleModal({
        activeModal: 'convertToTable',
        activeEntry: '',
        activeSheet: id,
        activeLeftPanelTab: leftPanelTabs.SCREEN,
      })
    )
  }

  const duplicateScreen = () => {
    setState(false)
    if (multiSectionId) {
      const allSheets = sectionSheets.map(({ id }) => id)
      dispatch(
        copyTableOrSimpleLoop({
          id,
          sectionId,
          multiSectionId,
          allSheets,
          allSheetsEntries: entries.filter(entry =>
            allSheets.includes(entry.sheet_id)
          ),
          seed: createGuid(),
        })
      )
    } else {
      dispatch(
        copySheet({
          id,
          sectionId,
          seed: createGuid(),
          parentSheet,
          sheetEntries,
        })
      )
    }
  }

  const siblings = parentSheet
    ? sectionSheets.filter(
        sheet => sheet.id !== id && sheet.section_id === sectionId
      )
    : []

  const deleteLoop = () => {
    setState(false)

    const keyField = entries.find(
      entry => isTrueStringMatch(entry.sheet_id, id) && entry.keyField
    )

    const directDescendants = sectionSheets
      .filter(sheet => isTrueStringMatch(sheet.section_id, multiSectionId))
      .sort((a, b) => a.position - b.position)

    const deleteScreenPayload = {
      id,
      sectionId,
      parentSheet,
      siblings,
      keyField,
      directDescendants: parentSheet
        ? directDescendants
        : directDescendants.map(sheet => ({
            ...sheet,
            // New sections need to be generated for each child when there's no parent sheet
            // because each child will now be a top level section
            newSectionId: guidNumber(),
          })),
    }

    dispatch(deleteLoopSheet(deleteScreenPayload))
  }

  const deleteScreen = () => {
    setState(false)

    let deleteScreenAction
    const additionalSheets = sectionSheets.filter(
      sheet => !isTrueStringMatch(sheet.id, id)
    )

    const deleteScreenPayload = {
      id,
      sectionId,
      parentSheet,
      deleteTopLevelSection: true,
      additionalSheets,
    }

    if (parentSheet) {
      if (siblings.length) {
        // Do not delete top level section
        deleteScreenPayload.deleteTopLevelSection = false
        // Delete any nested sheets if loop screen
        deleteScreenPayload.additionalSheets = multiSectionId
          ? sectionSheets.filter(
              sheet =>
                !isTrueStringMatch(sheet.id, id) &&
                !isTrueStringMatch(sheet.section_id, sectionId)
            )
          : []
      }
      deleteScreenAction = deleteChildSheet
    } else {
      deleteScreenAction = deleteSheet
    }

    dispatch(deleteScreenAction(deleteScreenPayload))
  }

  const convertToScreen = () => {
    setState(false)
    const convertToScreenPayload = {
      id,
    }
    dispatch(convertListToScreen(convertToScreenPayload))
  }

  const convertToList = () => {
    setState(false)
    const convertToListPayload = {
      id,
    }
    dispatch(convertScreenToList(convertToListPayload))
  }

  onClickOutside(sheetControlRef, () => setState(false))

  const sheetName = sheetType => {
    switch (sheetType) {
      case TABLE_SHEET:
        return 'Table'
      case LOOP_SHEET:
      case ADVANCED_LOOP_SHEET:
        return 'Loop'
      case LIST_SHEET:
        return (isNewWorkspace && 'List') || 'Screen'
      default:
        return 'Screen'
    }
  }

  const controlListOptions = [
    {
      label: `Duplicate ${sheetName(typeOfSheet)}`,
      onClick: duplicateScreen,
      visible:
        [NORMAL_SHEET, TABLE_SHEET, LIST_SHEET].includes(typeOfSheet) ||
        // The sectionSheets.length === 2 check shouldn't be necessary but Builder 3 currently isn't updating the
        // sheet_type_id to ADVANCED_LOOP_SHEET when an additional screen is added or the child screen is looped
        // in a LOOP_SHEET, so I added the check in for now to prevent the option from showing when it shouldn't.
        (typeOfSheet === LOOP_SHEET &&
          !parentSheet &&
          sectionSheets.length === 2),
    },
    {
      label: `Delete ${sheetName(typeOfSheet)}`,
      onClick: deleteScreen,
      visible: [NORMAL_SHEET, TABLE_SHEET, LIST_SHEET].includes(typeOfSheet),
    },
    {
      label: `Delete ${sheetName(typeOfSheet)}`,
      onClick: deleteLoop,
      visible: [LOOP_SHEET, ADVANCED_LOOP_SHEET].includes(typeOfSheet),
    },
    {
      label: `Convert to Table`,
      onClick: convertToTableModal,
      visible: isLoopable && !depth && typeOfSheet === NORMAL_SHEET, // only allow top-level sheets atm
    },
    {
      label: `Convert to Loop`,
      onClick: convertToLoopModal,
      visible: isLoopable && typeOfSheet === NORMAL_SHEET,
    },
    {
      label: `Convert to Screen`,
      onClick: deleteLoop,
      visible: typeOfSheet === TABLE_SHEET && isNewWorkspace, // only allow top-level sheets atm
    },
    {
      label: `Convert to Screen`,
      onClick: convertToScreen,
      visible: typeOfSheet === LIST_SHEET && isNewWorkspace,
    },
    {
      label: `Convert to List`,
      onClick: convertToList,
      visible:
        typeOfSheet === NORMAL_SHEET &&
        sheetEntries.length === 1 &&
        isEligibleKeyField(sheetEntries[0]) &&
        isNewWorkspace,
    },
  ].filter(({ visible }) => visible)

  return (
    <>
      <div className="control sheet--control">
        <BasicTooltip
          label="More Options"
          className="foundry--tooltip tooltip--top control--tooltip"
        />
        <div className="control--icon">
          <FontAwesomeIcon
            icon={faEllipsisH}
            className="sheet--more-options control--toggle"
            onClick={toggleControls}
            role="img"
          />
        </div>
      </div>
      {open ? (
        <ul className="foundry--surface control--list">
          {controlListOptions.map(({ label, onClick }, index) => (
            <li
              className="control--option"
              onClick={onClick}
              key={`control-option-${index}`}
            >
              <span className="control--option--label">{label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  )
}

export const SheetControls = props => {
  const { id, depth } = props
  const dispatch = useDispatch()
  const sheetControlRef = useRef(null)
  const handleScreenSettingsClick = e => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(openScreenSettings(id, depth))
  }

  return (
    <div className="sheet--controls" ref={sheetControlRef}>
      <div className="control sheet--control">
        <BasicTooltip
          label="Settings"
          className="foundry--tooltip tooltip--top control--tooltip"
        />
        <div className="control--icon">
          <FontAwesomeIcon
            className="sheet--settings"
            icon={faCog}
            onClick={handleScreenSettingsClick}
            role="img"
          />
        </div>
      </div>

      <SheetActions {...props} sheetControlRef={sheetControlRef} />
    </div>
  )
}

export const InlineFieldControls = ({ position, sheet_id }) => {
  const [controlType, setControlType] = useState('inline')
  const [openMenu, setOpenMenu] = useState(false)
  const dispatch = useDispatch()
  const entries = useSelector(entriesSelector)
  const sheets = useSelector(sheetsSelector)
  const controlRef = useRef(null)
  const { section_id } = sheets.find(sheet => sheet.id === sheet_id) || {}
  const parentSheet = sheets.find(sheet => sheet.multi_section == section_id)
  const sheetEntries = entries.filter(entry =>
    isTrueStringMatch(entry.sheet_id, sheet_id)
  )

  useHotkeys('esc', function (e) {
    setOpenMenu(false)
  })

  const addField = e => {
    e.stopPropagation()
    setOpenMenu(false)
    setControlType('field')
  }

  const addScreenBreak = e => {
    e.stopPropagation()
    setOpenMenu(false)
    setControlType('inline')

    dispatch(
      splitSheet({
        id: sheet_id,
        sectionId: section_id,
        position,
        parentSheet,
        sheetEntries,
      })
    )
  }
  onClickOutside(controlRef, () => {
    setOpenMenu(false)
    setControlType('inline')
  })

  const ControlsOpen = () => (
    <>
      <div className="in-between--control--list fields">
        <div className="btn--workspace--container">
          <PillButton
            className="add--screen"
            onClick={addField}
            label="ADD FIELD"
            icon="field"
          />
        </div>
        <div className="btn--workspace--container">
          <PillButton
            className="add--table"
            onClick={addScreenBreak}
            label="SCREEN BREAK"
            icon="page-break"
          />
        </div>
      </div>
    </>
  )

  const AddingField = () => (
    <div className="workspace--field field--partial">
      <QuickField
        sheetId={sheet_id}
        position={position + 1}
        additionalAction={() => {
          setOpenMenu(false)
          setControlType('field')
        }}
      />
    </div>
  )

  const controlStyles = {
    inline: `control--inline ${openMenu && 'active'}`,
    field: 'inline--new-field',
  }
  return (
    <span className={controlStyles[controlType]} ref={controlRef}>
      <>
        {controlType !== 'field' && (
          <div
            className="inline--toggle"
            onClick={e => {
              e.stopPropagation()
              setOpenMenu(!openMenu)
            }}
          >
            <FontAwesomeIcon size={'lg'} icon={faPlus} />
          </div>
        )}
        {openMenu && <ControlsOpen />}
        {controlType === 'field' && <AddingField />}
      </>
    </span>
  )
}

export const InlineScreenControls = ({ section_id }) => {
  const sheets = useSelector(sheetsSelector)
  const sections = useSelector(sectionsSelector)
  const entryTypes = useSelector(entryTypesSelector)
  const { position, depth } = sections.find(({ id }) => id === section_id) || {}
  const [openMenu, setOpenMenu] = useState(false)
  const controlRef = useRef(null)
  const dispatch = useDispatch()
  const insertSectionPosition = position + 1

  useHotkeys('esc', function (e) {
    setOpenMenu(false)
  })

  const insertScreen = e => {
    e.stopPropagation()
    setOpenMenu(false)

    dispatch(
      insertNewScreen({
        sectionPosition: insertSectionPosition,
        depth,
      })
    )
  }

  const insertTable = e => {
    e.stopPropagation()
    setOpenMenu(false)

    const textType = entryTypes.find(({ type }) => type === 'Text')
    dispatch(
      addTable({
        keyField: textType,
        count: sheets.length,
        sectionPosition: insertSectionPosition,
      })
    )
  }

  onClickOutside(controlRef, () => setOpenMenu(false))
  const builderType = useSelector(builderTypeSelector)

  return (
    <span
      ref={controlRef}
      className={`control--inline ${openMenu && 'active'}`}
    >
      <div
        className="inline--toggle"
        onClick={e => {
          e.stopPropagation()
          setOpenMenu(!openMenu)
        }}
      >
        <FontAwesomeIcon size={'lg'} icon={faPlus} />
      </div>
      {openMenu && (
        <div
          className="in-between--control--list"
          data-position={insertSectionPosition}
        >
          <div className="btn--workspace--container">
            <PillButton
              className="add--screen"
              onClick={insertScreen}
              label="ADD SCREEN"
              icon="screen"
            />
          </div>
          {builderType !== 'web-form' && (
            <div className="btn--workspace--container">
              <PillButton
                className="add--table"
                onClick={insertTable}
                label="ADD TABLE"
                icon="table"
              />
            </div>
          )}
        </div>
      )}
    </span>
  )
}

export const DatePicker = ({
  guid,
  className,
  dateFormat,
  allowInput,
  enableTime,
  defaultDate,
  update = () => {},
  required,
  readOnly,
  label,
  error = false,
  errorText = '',
  onKeyDown,
  onBlur,
}) => {
  const handleUpdate = (selectedDates, dateStr, instance) => {
    update(dateStr)
  }
  const filteredDate = defaultDate === 'BLANK' ? '' : defaultDate
  return (
    <>
      <Flatpickr
        key={`${guid}-${defaultDate}`}
        className={`${className} ${error && 'invalid'}`}
        value={filteredDate}
        options={{
          allowInput: allowInput,
          dateFormat: dateFormat,
          enableTime: enableTime,
          defaultDate: filteredDate,
          disableMobile: true,
          allowInput: false,
          enableTime: false,
          static: true,
        }}
        onChange={handleUpdate}
        onClose={onBlur}
        disabled={readOnly}
        onKeyDown={onKeyDown}
      />
      <label className={`input-fields__label`}>
        {required ? `${label}*` : `${label}`}
      </label>
      {error && (
        <div className="input-fields__error">
          <i className="input-fields__error-icon foundry-icon icon--solid icon--exclamation-triangle icon--red"></i>
          <span className="input-fields__error-message">{errorText}</span>
        </div>
      )}
    </>
  )
}

export const TimePicker = ({
  guid,
  className,
  format,
  style,
  defaultDate,
  update = () => {},
  required,
  readOnly,
  label = '',
  error,
  errorText,
}) => {
  const [updateCount, incrementCount] = useState(0)
  const invalidClass = error && error.length ? 'invalid' : ''

  let options = {
    enableTime: true,
    noCalendar: true,
    minuteIncrement: 1,
    dateFormat: format,
    altFormat: format,
    altInput: true,
    static: true,
    disableMobile: true,
    time_24hr: style === 1 ? false : true,
    enableSeconds: style === 2 || style === 3 ? true : false,
    altInputClass: `${className} time-altInput ${invalidClass}`,
  }

  const handleUpdate = (selectedDates, dateStr, instance) => {
    const selectedTime = selectedDates[0]
    incrementCount(updateCount + 1)

    update(selectedTime)
  }

  return (
    <>
      <Flatpickr
        id={`hidden_timepicker_input-${guid}`}
        key={`${guid}-${defaultDate}-${style}-${updateCount}`}
        className={`${className} ${invalidClass}`}
        value={defaultDate}
        data-enable-time
        options={options}
        onOpen={(selectedDates, dateStr, instance) => {
          //Meh kinda method...flatpickr currently have no disable specific hours/minutes/seconds feature
          //So have to access the input's DOM to hide
          const disableHourDisplay = () => {
            instance.hourElement.parentElement.style.display = 'none'
            instance.hourElement.parentElement.nextSibling.style.display =
              'none'
          }
          //Disable + hide Hour display for MM:SS style
          style == 3 ? disableHourDisplay() : ''
        }}
        onClose={handleUpdate}
        disabled={readOnly}
      />
      <label className={`input-fields__label`}>
        {required ? `${label}*` : `${label}`}
      </label>
      {error && (
        <div className="input-fields__error">
          <i className="input-fields__error-icon icon--solid foundry-icon icon--exclamation-triangle icon--red"></i>
          <span className="input-fields__error-message">{errorText}</span>
        </div>
      )}
    </>
  )
}

// TODO: remove this component once new workspace launches
export const HasReferenceData = ({ guid = '', additionalClass = '' }) => {
  const refData = useSelector(referenceDataEntries)
  const hasRefData = refData.find(({ guid: refGuid }) => {
    return guid == refGuid
  })
  const ReferenceDataWrapper = ({
    additionalClass,
    wrapperElement,
    children,
  }) => (additionalClass ? wrapperElement(children) : children)
  return (
    <>
      {hasRefData && (
        <ReferenceDataWrapper
          additionalClass={additionalClass}
          wrapperElement={children => (
            <div className={additionalClass}>{children}</div>
          )}
        >
          <FontAwesomeIcon icon={faBolt} className="label--icon" />
        </ReferenceDataWrapper>
      )}
    </>
  )
}

export const HasReferenceDataIcon = ({ guid = '', additionalClass = '' }) => {
  const refData = useSelector(referenceDataEntries)
  const hasRefData = refData.find(({ guid: refGuid }) => {
    return guid == refGuid
  })
  const ReferenceDataWrapper = ({
    additionalClass,
    wrapperElement,
    children,
  }) => (additionalClass ? wrapperElement(children) : children)
  return <>{hasRefData && <ReferenceDataIcon />}</>
}

export const HasLiveDataIcon = ({ guid = '', additionalClass = '' }) => {
  const liveData = useSelector(liveDataEntries)
  const hasLiveData = liveData.find(({ guid: liveGuid }) => {
    return guid == liveGuid
  })
  const LiveDataWrapper = ({ additionalClass, wrapperElement, children }) =>
    additionalClass ? wrapperElement(children) : children
  return <>{hasLiveData && <LiveDataIcon />}</>
}

// TODO: remove this component once new workspace launches
export const HasCondition = ({
  guid = '',
  sheetId = '',
  additionalClass = '',
}) => {
  const conditions = useSelector(conditionsSelector)
  const hasCondition = conditions.find(({ parentEntryGuid, sheet_id }) => {
    return (
      isTrueStringMatch(parentEntryGuid, guid) ||
      (!parentEntryGuid && isTrueStringMatch(sheet_id, sheetId))
    )
  })
  const ConditionalWrapper = ({ additionalClass, wrapperElement, children }) =>
    additionalClass ? wrapperElement(children) : children

  return (
    <>
      {hasCondition && (
        <ConditionalWrapper
          additionalClass={additionalClass}
          wrapperElement={children => (
            <div className={additionalClass}>{children}</div>
          )}
        >
          <FontAwesomeIcon icon={faCodeBranch} className="label--icon" />
        </ConditionalWrapper>
      )}
    </>
  )
}

export const HasConditionIcon = ({
  guid = '',
  sheetId = '',
  additionalClass = '',
}) => {
  const conditions = useSelector(conditionsSelector)
  const hasCondition = conditions.find(({ parentEntryGuid, sheet_id }) => {
    return (
      isTrueStringMatch(parentEntryGuid, guid) ||
      (!parentEntryGuid && isTrueStringMatch(sheet_id, sheetId))
    )
  })
  const ConditionalWrapper = ({ additionalClass, wrapperElement, children }) =>
    additionalClass ? wrapperElement(children) : children

  return <>{hasCondition && <ConditionIcon />}</>
}

export const SavePublishUsers = ({ id, name, email, assigned, checkAll }) => {
  const dispatch = useDispatch()
  const [checked, setInput] = useState(!checkAll)
  return (
    <div className="save-publish--col">
      <FancyCheckbox
        checked={checked || checkAll}
        onChange={() => {
          setInput(!checked)
        }}
        label={name}
        id={id}
      />
      <div>{email}</div>
    </div>
  )
}
